import { useLocale, useT } from "../i18n";
import { Seo } from "../components/Seo";
import { localized, useSiteSettings } from "../lib/siteSettings";
import { Area, FormFooter, FormGroup, FormLayout, PageHero, Success, Text, check, useRequestForm } from "../components/forms/FormKit";

const P = "contact";

const Details = () => {
  const t = useT();
  const lang = useLocale();
  const s = useSiteSettings();
  const address = localized(s.company?.address, lang);
  const phone = s.phone && s.phone !== "To be provided" ? s.phone : "";
  const rows = [["form.phone", phone, phone && `tel:${phone.replace(/[^\d+]/g, "")}`, "phone"], ["form.email", s.public_email, s.public_email && `mailto:${s.public_email}`, "email"],
    ["contactPage.address", address, null, "address"]].filter(([, v]) => v);
  return (
    <div data-testid="contact-details">
      <p className="micro-label text-ink/45">{t("contactPage.details")}</p>
      {rows.length > 0 && (
        <dl className="mt-6 border-t border-ink/15">
          {rows.map(([label, value, href, id]) => (
            <div key={id} className="border-b border-ink/15 py-4" data-testid={`contact-detail-${id}`}>
              <dt className="text-xs text-ink/50">{t(label)}</dt>
              <dd className="mt-1 whitespace-pre-line">{href ? <a href={href} className="underline-offset-4 hover:underline">{value}</a> : value}</dd>
            </div>
          ))}
        </dl>
      )}
      {address && (
        <div className="mt-8 aspect-[4/3] bg-mist" data-testid="contact-map">
          <iframe title={t("contactPage.map")} src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`} loading="lazy"
            referrerPolicy="no-referrer-when-downgrade" className="h-full w-full border-0 grayscale" />
        </div>
      )}
    </div>
  );
};

export default function ContactPage() {
  const t = useT();
  const lang = useLocale();
  const ctx = useRequestForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "" });
  const { form } = ctx;
  const submit = (e) => {
    e.preventDefault();
    const errs = check(form, { name: { req: true, max: 100 }, email: { req: true, email: true, max: 200 }, phone: { max: 40 },
      subject: { req: true, max: 150 }, message: { req: true, max: 3000 } }, t);
    ctx.send("/requests/contact", errs, (fill_ms) => ({
      locale: lang, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), subject: form.subject.trim(), message: form.message.trim(),
      consent: form.consent, website: form.website, fill_ms,
    }));
  };
  return (
    <div data-testid="contact-page">
      <Seo title={t("pages.contact.title")} description={t("pages.contact.description")} path="contact" />
      <PageHero eyebrow={t("contactPage.eyebrow")} title={t("contactPage.title")} intro={t("contactPage.intro")} />
      <FormLayout aside={<Details />}>
        {ctx.phase === "success" ? <Success prefix={P} onAgain={ctx.reset} /> : (
          <form onSubmit={submit} noValidate className="grid gap-10" data-testid="contact-form">
            <FormGroup title={t("form.yourDetails")}>
              <div className="grid gap-5 sm:grid-cols-2">
                <Text ctx={ctx} prefix={P} k="name" label={t("form.name")} autoComplete="name" className="sm:col-span-2" />
                <Text ctx={ctx} prefix={P} k="email" label={t("form.email")} type="email" autoComplete="email" inputMode="email" />
                <Text ctx={ctx} prefix={P} k="phone" label={t("form.phone")} type="tel" autoComplete="tel" optional />
                <Text ctx={ctx} prefix={P} k="subject" label={t("contactPage.subject")} className="sm:col-span-2" />
              </div>
              <Area ctx={ctx} prefix={P} k="message" label={t("form.message")} rows={7} />
            </FormGroup>
            <FormFooter ctx={ctx} prefix={P} submitLabel={t("contactPage.submit")} />
          </form>
        )}
      </FormLayout>
    </div>
  );
}
